import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ConsultationModal } from "./ConsultationModal";
import { LANDING_HERO_VIDEOS } from "../data/landingHeroVideos";

const Navbar = ({ color = "white", customColor = null, heroSlideIndex }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname === "/";

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
  }, [isOpen]);

  const primaryNavLinks = [
    { name: "Home", href: "/", type: "route" },
    { name: "What We Do", href: "/what-we-do", type: "route" },
    { name: "Custom Solutions", href: "/custom-solutions", type: "route" },
    { name: "Case Studies", href: "/case-studies", type: "route" },
  ];

  const secondaryNavLinks = [
    { name: "Products", href: "/products", type: "route" },
    { name: "About", href: "/about", type: "route" },
    { name: "Blog", href: "/blog", type: "route" },
    { name: "Contact", href: "/contact-us", type: "route" },
  ];

  const serviceLinks = [
    { name: "AI Agents", href: "/services/ai-agents", type: "route" },
    { name: "AI Automation", href: "/services/ai-automation", type: "route" },
    { name: "AI Integration", href: "/services/ai-integration", type: "route" },
    {
      name: "AI Consultancy",
      href: "/services/ai-consultancy",
      type: "route",
    },
    { name: "All capabilities", href: "/what-we-do", type: "route" },
  ];

  const scrollToSection = useCallback((sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setIsOpen(false);
    }
  }, []);

  const handleNavClick = (link) => {
    if (link.type === "route") {
      navigate(link.href);
      setIsOpen(false);
    } else if (link.type === "anchor") {
      scrollToSection(link.href);
    }
  };

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const showHeroShortcutNav =
    typeof heroSlideIndex === "number" && location.pathname === "/";

  const renderHeroShortcutNav = () => {
    if (!showHeroShortcutNav) return null;
    return (
      <nav
        aria-label="Hero sections"
        className="hidden md:flex items-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 min-w-0 shrink"
      >
        {LANDING_HERO_VIDEOS.map((v, idx) => {
          const active = idx === heroSlideIndex;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(v.path);
              }}
              className={`m-0 cursor-pointer truncate max-w-[34vw] sm:max-w-none border-0 bg-transparent p-0 text-sm sm:text-base md:text-lg font-normal transition-colors focus:outline-none focus-visible:underline ${
                active
                  ? "text-white font-medium"
                  : "text-white/70 hover:text-white"
              }`}
            >
              <span className="sm:hidden">{v.navShortLabel}</span>
              <span className="hidden sm:inline">{v.title}</span>
            </button>
          );
        })}
      </nav>
    );
  };

  return (
    <>
      <ConsultationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Standard Navbar - Fades out on scroll */}
      <nav
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
          scrolled
            ? "opacity-0 -translate-y-full pointer-events-none"
            : "opacity-100 translate-y-0"
        } ${
          customColor
            ? "backdrop-blur-none"
            : "bg-transparent backdrop-blur-none"
        }`}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
        }}
      >
        <div className="flex justify-between items-center h-36 px-12 sm:h-42 sm:px-16 md:px-36 lg:px-48 xl:px-60 max-w-full">
          {/* Logo - Left Side (Hidden on Mobile) */}
          <a
            href="/"
            className={`font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl transition-all duration-200 flex-shrink-0 logo-font relative group block px-4 py-2 rounded-lg ${
              isLandingPage
                ? customColor
                  ? "text-black hover:text-black"
                  : "text-white/90 hover:text-white"
              : color === "white"
              ? "text-white hover:text-white/80"
              : "text-black hover:text-gray-700"
            }`}
          >
            Jamil Global
            {/* Decorative underline */}
            <div
              className={`absolute bottom-0 left-0 h-0.5 w-full transition-all duration-300 transform scale-x-0 group-hover:scale-x-100 ${
                isLandingPage
                  ? customColor
                    ? "bg-black group-hover:bg-black"
                    : "bg-white/70 group-hover:bg-white/90"
                  : color === "white"
                  ? "bg-white group-hover:bg-white/90"
                  : "bg-black group-hover:bg-gray-700"
              }`}
            />
            {/* Always visible thin line */}
            <div
              className={`absolute bottom-0 left-0 h-px w-full transition-all duration-300 ${
                isLandingPage
                  ? customColor
                    ? "bg-black/30"
                    : "bg-white/30"
                  : color === "white"
                  ? "bg-white/30"
                  : "bg-black/30"
              }`}
            />
          </a>

          {/* Right Side: Hero shortcuts + Hamburger */}
          <div className="flex items-center gap-4 sm:gap-5 md:gap-7 flex-shrink-0">
            {renderHeroShortcutNav()}
            {/* Hamburger Button - Right Side (Always Visible) */}
            <button
              className="flex flex-col justify-center items-center group p-2 relative
                         w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16
                         rounded-lg flex-shrink-0"
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              <span
                className={`block rounded transition-all duration-300 ease-out
                         w-8 h-[4px] sm:w-10 sm:h-[4px] md:w-12 md:h-[4px] lg:w-14 lg:h-[4px]
                         ${
                           isOpen
                             ? "rotate-45 translate-y-0.5 sm:translate-y-1 md:translate-y-1.5 bg-black"
                             : scrolled
                             ? "bg-black group-hover:bg-gray-600 -translate-y-0.2 sm:-translate-y-1.1 md:-translate-y-1.8"
                             : customColor === "#ffffff"
                             ? "bg-black group-hover:bg-gray-600 -translate-y-0.2 sm:-translate-y-1.1 md:-translate-y-1.8"
                             : customColor
                             ? "bg-black group-hover:bg-gray-600 -translate-y-0.2 sm:-translate-y-1.1 md:-translate-y-1.8"
                             : color === "white"
                             ? "bg-white group-hover:bg-gray-600 -translate-y-0.2 sm:-translate-y-1.1 md:-translate-y-1.8"
                             : "bg-black group-hover:bg-gray-600 -translate-y-0.2 sm:-translate-y-1.1 md:-translate-y-1.8"
                         }`}
                style={
                  customColor && !isOpen && !scrolled
                    ? { backgroundColor: customColor }
                    : {}
                }
              />
              <span
                className={`block rounded transition-all duration-300 ease-out
                         w-8 h-[4px] sm:w-10 sm:h-[4px] md:w-12 md:h-[4px] lg:w-14 lg:h-[4px]
                         ${
                           isOpen
                             ? "-rotate-45 -translate-y-0.5 sm:-translate-y-1 md:-translate-y-1.5 bg-black"
                             : scrolled
                             ? "bg-black group-hover:bg-gray-600 translate-y-1 sm:translate-y-1.1 md:translate-y-1.8"
                             : customColor === "#ffffff"
                             ? "bg-black group-hover:bg-gray-600 translate-y-1 sm:translate-y-1.1 md:translate-y-1.8"
                             : customColor
                             ? "bg-black group-hover:bg-gray-600 translate-y-1 sm:translate-y-1.1 md:translate-y-1.8"
                             : color === "white"
                             ? "bg-white group-hover:bg-gray-600 translate-y-1 sm:translate-y-1.1 md:translate-y-1.8"
                             : "bg-black group-hover:bg-gray-600 translate-y-1 sm:translate-y-1.1 md:translate-y-1.8"
                         }`}
                style={
                  customColor && !isOpen && !scrolled
                    ? { backgroundColor: customColor }
                    : {}
                }
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Scrolled / Collapsed "Pill" Navbar */}
      <div
        className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999] transition-all duration-500 ease-in-out ${
          scrolled
            ? "translate-y-0 opacity-100"
            : "-translate-y-24 opacity-0 pointer-events-none"
        } w-[90%] sm:w-auto max-w-lg sm:max-w-none`}
      >
        <div className="bg-white/90 backdrop-blur-md shadow-lg rounded-full py-1.5 sm:py-2 px-3 sm:px-6 md:px-8 flex items-center justify-between w-full sm:w-auto gap-2">
          {/* Logo on Left */}
          <a
            href="/"
            className="font-bold text-xl sm:text-2xl md:text-4xl text-black leading-none hover:text-gray-700 transition-colors logo-font whitespace-nowrap mr-4 sm:mr-8"
          >
            Jamil Global
          </a>

          {/* Right Side: Action buttons + Hamburger */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3 min-w-0">
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-black hover:bg-gray-800 text-white rounded-full px-2.5 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-xs font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Book a Call
              </button>
              <button
                onClick={() => navigate("/contact-us")}
                className="bg-gray-100 sm:bg-transparent hover:bg-gray-200 sm:hover:bg-gray-100 text-black rounded-full px-2.5 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-xs font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Contact Us
              </button>
            </div>

            {/* Hamburger in Pill */}
            <button
              className="flex flex-col justify-center items-center group p-1.5 sm:p-2 relative
                             w-7 h-7 sm:w-9 sm:h-9 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              <div className="w-3.5 sm:w-5 flex flex-col items-end gap-[3px] sm:gap-[5px]">
                <span
                  className={`block h-[1.5px] sm:h-[2px] w-full bg-black rounded-full transition-all duration-300 ${
                    isOpen
                      ? "rotate-45 translate-y-[2.5px] sm:translate-y-[3.5px]"
                      : ""
                  }`}
                />
                <span
                  className={`block h-[1.5px] sm:h-[2px] w-full bg-black rounded-full transition-all duration-300 ${
                    isOpen
                      ? "-rotate-45 -translate-y-[2.5px] sm:-translate-y-[3.5px]"
                      : ""
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen Right-Side Sliding Menu */}
      <div
        className={`fixed inset-0 bg-white z-[2000] transform transition-transform duration-500 ease-in-out overflow-hidden ${
          isOpen ? "translate-x-0" : "translate-x-full opacity-0"
        }`}
      >
        <div className="flex flex-col h-[100dvh] min-h-0 w-full relative">
          {/* Navigation — vertically centered; scrolls if content exceeds viewport */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full">
            <div className="min-h-full flex flex-col justify-center px-4 sm:px-8 md:px-12 py-12 sm:py-16 md:py-20">
              <div className="w-full max-w-6xl mx-auto flex flex-col items-center z-[2004] gap-8 sm:gap-10 md:gap-12">
              {/* Primary destinations */}
              <section
                className="w-full flex flex-col items-center gap-3 sm:gap-4"
                aria-label="Main pages"
              >
                <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 md:gap-4 lg:gap-8">
                  {primaryNavLinks.map((link, index) => (
                    <button
                      key={link.name}
                      onClick={() => handleNavClick(link)}
                      className={`group relative px-5 py-2 sm:px-6 sm:py-3 rounded-lg transition-all duration-200 whitespace-nowrap
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20
                               hover:scale-[1.02] active:scale-[0.98]
                               z-[2003] ${
                                 isOpen
                                   ? "translate-x-0 opacity-100"
                                   : "translate-x-8 opacity-0"
                               }`}
                      style={{
                        transitionDelay: isOpen ? `${index * 100}ms` : "0ms",
                        background: "transparent",
                      }}
                    >
                      <span
                        className="relative z-10 text-xl sm:text-lg md:text-2xl font-medium
                               transition-colors duration-200 text-black group-hover:text-gray-800
                               text-center tracking-tight"
                      >
                        {link.name}
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              <div
                className={`w-full max-w-md h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent transition-opacity duration-300 ${
                  isOpen ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  transitionDelay: isOpen ? `${primaryNavLinks.length * 100}ms` : "0ms",
                }}
                aria-hidden
              />

              {/* Service capabilities */}
              <section
                className="w-full flex flex-col items-center gap-4 sm:gap-5"
                aria-labelledby="menu-services-heading"
              >
                <h2
                  id="menu-services-heading"
                  className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-gray-400 transition-all duration-300 ${
                    isOpen
                      ? "translate-x-0 opacity-100"
                      : "translate-x-6 opacity-0"
                  }`}
                  style={{
                    transitionDelay: isOpen
                      ? `${primaryNavLinks.length * 100}ms`
                      : "0ms",
                  }}
                >
                  Lead offers
                </h2>
                <div className="grid grid-cols-2 w-full max-w-2xl mx-auto gap-x-3 gap-y-2 sm:grid-cols-3 sm:gap-x-4 sm:gap-y-3 md:gap-x-5 md:gap-y-3 lg:flex lg:flex-row lg:flex-wrap lg:justify-center lg:max-w-none lg:gap-x-8">
                  {serviceLinks.map((link, index) => {
                    const serviceDelayBase = primaryNavLinks.length + 1;
                    return (
                      <button
                        key={link.name}
                        onClick={() => handleNavClick(link)}
                        className={`relative px-3 py-2.5 sm:px-5 sm:py-2.5 rounded-md transition-all duration-200
                               whitespace-normal sm:whitespace-nowrap w-full lg:w-auto
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-black/15
                               hover:scale-[1.02] active:scale-[0.98]
                               z-[2003] ${
                                 isOpen
                                   ? "translate-x-0 opacity-100"
                                   : "translate-x-8 opacity-0"
                               }`}
                        style={{
                          transitionDelay: isOpen
                            ? `${(serviceDelayBase + index) * 100}ms`
                            : "0ms",
                          background: "transparent",
                        }}
                      >
                        <span
                          className="relative z-10 text-base sm:text-base md:text-lg font-medium
                               transition-colors duration-200 text-gray-600 hover:text-black
                               text-center block leading-snug"
                        >
                          {link.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <div
                className={`w-full max-w-md h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent transition-opacity duration-300 ${
                  isOpen ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  transitionDelay: isOpen
                    ? `${(primaryNavLinks.length + serviceLinks.length) * 100}ms`
                    : "0ms",
                }}
                aria-hidden
              />

              {/* About, Blog, Contact */}
              <section
                className="w-full flex flex-col items-center gap-4 sm:gap-5 pb-6"
                aria-labelledby="menu-company-heading"
              >
                <h2
                  id="menu-company-heading"
                  className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-gray-400 transition-all duration-300 ${
                    isOpen
                      ? "translate-x-0 opacity-100"
                      : "translate-x-6 opacity-0"
                  }`}
                  style={{
                    transitionDelay: isOpen
                      ? `${
                          (primaryNavLinks.length + serviceLinks.length) * 100
                        }ms`
                      : "0ms",
                  }}
                >
                  Company
                </h2>
                <div className="grid grid-cols-2 w-full max-w-sm mx-auto gap-x-4 gap-y-2 sm:max-w-none sm:flex sm:flex-row sm:flex-wrap sm:justify-center sm:gap-1 md:gap-4 lg:gap-8">
                  {secondaryNavLinks.map((link, index) => (
                    <button
                      key={link.name}
                      onClick={() => handleNavClick(link)}
                      className={`relative px-5 py-2 sm:px-6 sm:py-3 rounded-lg transition-all duration-200
                               whitespace-nowrap w-full sm:w-auto
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20
                               hover:scale-[1.02] active:scale-[0.98]
                               z-[2003] ${
                                 isOpen
                                   ? "translate-x-0 opacity-100"
                                   : "translate-x-8 opacity-0"
                               }`}
                      style={{
                        transitionDelay: isOpen
                          ? `${
                              (primaryNavLinks.length +
                                serviceLinks.length +
                                1 +
                                index) *
                              100
                            }ms`
                          : "0ms",
                        background: "transparent",
                      }}
                    >
                      <span
                        className="relative z-10 text-base sm:text-base md:text-lg font-medium
                               transition-colors duration-200 text-neutral-800 hover:text-black
                               text-center block"
                      >
                        {link.name}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
              </div>
            </div>
          </div>

          {/* SVG Image Section */}
          <div className="w-full max-w-10xl mx-auto sm:-mb-28 opacity-50 absolute bottom-1 left-10">
            <img
              src="/pictures/pattern.svg"
              alt="Decorative pattern"
              className="w-full h-auto transform -scale-x-400"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
